import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cx } from './cx';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'inverse';
export type ButtonSize = 'sm' | 'md' | 'lg';

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
  /** Stretch to the container width. */
  block?: boolean;
};

const VARIANT: Record<ButtonVariant, string> = {
  // Flat, confident fill — the app's GradientButton went flat aubergine too.
  primary: 'bg-primary text-on-primary hover:bg-primary-hover border border-transparent',
  secondary: 'bg-surface-lowest text-ink border border-hairline-strong hover:bg-surface-low',
  ghost: 'bg-transparent text-accent hover:bg-primary-container-soft border border-transparent',
  danger: 'bg-error-container text-error border border-transparent hover:brightness-95',
  // For use on `surface-high` (hero cards).
  inverse: 'bg-inverse-fill text-inverse border border-inverse-hairline hover:bg-inverse-fill-hover',
};

const SIZE: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-body-sm rounded-sm gap-1.5',
  md: 'h-9 px-3.5 text-body-sm rounded-control gap-2',
  lg: 'h-11 px-[17px] text-body rounded-md gap-2',
};

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { variant = 'primary', size = 'md', loading = false, icon, block, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cx(
        'inline-flex items-center justify-center font-medium whitespace-nowrap transition-colors select-none',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        VARIANT[variant],
        SIZE[size],
        block && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : icon}
      {children}
    </button>
  );
});

/** Text-only inline action (links inside copy, "See all"). */
export function TextButton({ className, children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={cx('text-body-sm font-semibold text-accent hover:text-accent-deep underline-offset-4 hover:underline', className)}
      {...rest}
    >
      {children}
    </button>
  );
}
